// auth.svelte.ts — Auth0 identity for the SPA (auth.md "Frontend", infra.md D10).
//
// Replaces the dev `:user` path-param stand-in (the old user.ts switcher) with
// a verified Auth0 login. The browser logs in via Auth0 (Google); the manager
// validates the JWT and derives identity from the verified `sub`. Every REST
// call carries the access token on the `Authorization` header and the WS URL
// carries it as `?access_token=` (browsers can't set WS headers), so the single
// source of identity is the token — never a spoofable URL segment.
//
// All of this is browser-only: the Auth0Client touches `window`, `localStorage`
// and the history API. We guard every entry point with SvelteKit's `browser`
// flag so the module is import-safe during SSR (it's only ever *used* after the
// layout mounts in the browser).

import { browser } from '$app/environment';
import {
	PUBLIC_AUTH0_DOMAIN,
	PUBLIC_AUTH0_CLIENT_ID,
	PUBLIC_AUTH0_AUDIENCE,
} from '$env/static/public';
import { env as publicEnv } from '$env/dynamic/public';
import { Auth0Client, type Auth0Client as Auth0ClientType, type User } from '@auth0/auth0-spa-js';
// type-only: cycle-free even though api.ts (where the value-side lives) imports
// authHeaders from this module. The runtime call uses an inlined backend URL +
// the local authHeaders below — see the cycle-prevention note on loadMe().
import type { Me } from './marketplace-api';

// ── Dev bypass (auth.md "Dev bypass") ─────────────────────────────────────────
// When PUBLIC_DEV_NO_AUTH === 'true' the SPA SKIPS Auth0 entirely and presents a
// dev identity, mirroring the manager's DEV_NO_AUTH bypass (?dev_user= /
// x-sayano-user). This is what lets a local run and the browser E2E work without
// an interactive Google login — the symmetric frontend half of the manager flag.
//
// SECURITY: this is a build-time flag, UNSET/false in prod (→ real Auth0; missing
// var resolves to false, so the secure default is automatic). And it is NOT the
// enforcement boundary: even a prod bundle mis-built with it on can't open a hole,
// because the manager in prod runs DEV_NO_AUTH=false and rejects every token-less
// (x-sayano-user / dev_user) request with 401. The two flags must BOTH be on, and
// the manager additionally requires a loopback bind for its half (env.ts refine).
const DEV_BYPASS = publicEnv.PUBLIC_DEV_NO_AUTH === 'true';
const DEV_USER_KEY = 'sayano_dev_user';

function devUser(): string {
	if (!browser) return 'leon';
	return localStorage.getItem(DEV_USER_KEY) || 'leon';
}

// Switch the simulated dev user for multi-user E2E (manager keys everything on
// this identity). No-op outside the bypass. Reload after calling to re-init.
export function setDevUser(user: string): void {
	if (!browser || !DEV_BYPASS) return;
	localStorage.setItem(DEV_USER_KEY, user);
}

export function isDevBypass(): boolean {
	return DEV_BYPASS;
}

// The shape of the profile fields the UI renders (sidebar footer + marketplace
// header). `sub` is the verified identity — the same string the manager derives
// from the JWT and uses as the DB user_id / loadout key — but on the client it's
// for DISPLAY only (it never builds a URL anymore).
export interface AuthProfile {
	sub: string;
	email?: string;
	name?: string;
}

// ── reactive auth state ──────────────────────────────────────────────────────
// `$state` in a `.svelte.ts` module is shared app-wide: the layout's login gate,
// the sidebar footer and the marketplace header all read the same instance.
const state = $state({
	ready: false as boolean, // initAuth() has finished (callback handled + state loaded)
	isAuthenticated: false as boolean,
	profile: null as AuthProfile | null,
	// Onboarding (roadmap.md #4): /me fetched separately from auth so the layout
	// can show a brief spinner between "authenticated" and "we know if onboarded".
	// On a /me failure `me` stays null but `meError` flips true — the layout shows
	// a 'reconnect' state, NOT the wizard. A failed /me must never be read as
	// "not onboarded", or a transient outage during a redeploy re-triggers
	// onboarding for already-onboarded users ("onboarding restarts each redeploy").
	me: null as Me | null,
	meReady: false as boolean,
	meError: false as boolean,
});

// One Auth0Client for the whole tab. Built lazily on first browser use so the
// module stays import-safe under SSR (the constructor reads `window`).
let client: Auth0ClientType | null = null;

function getClient(): Auth0ClientType {
	if (client) return client;
	client = new Auth0Client({
		domain: PUBLIC_AUTH0_DOMAIN,
		clientId: PUBLIC_AUTH0_CLIENT_ID,
		authorizationParams: {
			audience: PUBLIC_AUTH0_AUDIENCE,
			// Auth0 redirects back to the SPA origin; the same origin is registered
			// as an allowed callback for the "Sayano-AI Web" app (auth.md Config).
			redirect_uri: window.location.origin,
		},
		// Refresh tokens + localStorage cache so getTokenSilently() can mint a
		// fresh access token for every API/WS call without a redirect, and the
		// session survives a page reload (F5 lands authenticated, no re-login).
		useRefreshTokens: true,
		// Fall back to a silent iframe (/authorize?prompt=none) when no usable
		// refresh token is cached, instead of throwing "Missing Refresh Token".
		// This self-heals sessions that predate the API's `allow_offline_access`
		// being enabled (they hold an access token but no RT): the fallback mints
		// a fresh token AND — now that offline_access is on — seeds a refresh
		// token, so subsequent calls use the RT path. Without it, getTokenSilently
		// hard-errors the catalog/WS the moment the 24h access token expires.
		useRefreshTokensFallback: true,
		cacheLocation: 'localstorage',
	});
	return client;
}

function toProfile(u: User | undefined): AuthProfile | null {
	if (!u || !u.sub) return null;
	return { sub: u.sub, email: u.email, name: u.name };
}

// ── public API ───────────────────────────────────────────────────────────────

// Backend origin for the /me probe. Inlined (not imported from ./api) so
// auth.svelte stays free of the ./api → ./auth.svelte → ./api cycle that an
// `import { API_ORIGIN }` would create. Same default as api.ts.
function backendOrigin(): string {
	const raw = (import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:8787') as string;
	return raw.replace(/\/$/, '');
}

// Load /me into the auth store. Driven from initAuth() once we know the user is
// signed in, so the manager's resolveUser() always finds a bearer / dev-bypass.
// Retries with backoff across the manager's redeploy restart window so a transient
// /me outage self-heals into the app (the layout shows the 'me-pending' spinner
// while we retry) instead of flashing the wizard. Only after the retries are
// exhausted do we flip `meError` → the layout shows a 'reconnect' state (never the
// wizard) and a Retry/auto-retry can recover it once the manager is back.
async function loadMe(): Promise<void> {
	const MAX_ATTEMPTS = 6;
	for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
		try {
			// Carry the Auth0 display name/email so the manager can store them for the
			// admin People roster (the access token it verifies only carries the `sub`).
			// Display-only; URL-encoded to stay header-safe. The manager trusts these for
			// labels, never for identity (which comes from the verified token).
			const headers = await authHeaders();
			const p = state.profile;
			if (p?.name) headers['x-sayano-name'] = encodeURIComponent(p.name);
			if (p?.email) headers['x-sayano-email'] = encodeURIComponent(p.email);
			const res = await fetch(`${backendOrigin()}/me`, { headers });
			if (!res.ok) throw new Error(`/me ${res.status}`);
			state.me = (await res.json()) as Me;
			state.meError = false;
			state.meReady = true;
			return;
		} catch (e) {
			if (attempt === MAX_ATTEMPTS) {
				console.error('loadMe failed after retries', e);
				state.me = null;
				state.meError = true;
				state.meReady = true;
				return;
			}
			// Backoff: 0.5s, 1s, 2s, 4s, 4s ≈ covers a manager redeploy swap.
			await new Promise((r) => setTimeout(r, Math.min(4000, 500 * 2 ** (attempt - 1))));
		}
	}
}

// Re-attempt /me (the layout's reconnect Retry + auto-retry). Resets to the
// pending state so the spinner shows during the re-fetch.
export async function reloadMe(): Promise<void> {
	state.meReady = false;
	state.meError = false;
	await loadMe();
}

// Called once from +layout.svelte on mount. If we're landing on the Auth0
// redirect (the `?code=…&state=…` query), complete the code exchange and strip
// those params from the URL; then load the current auth state into the reactive
// store. Idempotent and SSR-safe (no-op off the browser).
export async function initAuth(): Promise<void> {
	if (!browser) return;
	if (DEV_BYPASS) {
		// No Auth0 round-trip: present the dev identity straight away so the login
		// gate is skipped and the app renders as `devUser()`.
		const u = devUser();
		state.isAuthenticated = true;
		state.profile = { sub: u, email: `${u}@dev.local`, name: u };
		state.ready = true;
		// Pull /me to learn the onboarded flag; the layout shows a spinner until
		// state.meReady flips.
		void loadMe();
		return;
	}
	const c = getClient();
	try {
		const params = new URLSearchParams(window.location.search);
		if (params.has('code') && params.has('state')) {
			await c.handleRedirectCallback();
			// Drop the auth params so a reload doesn't re-trigger the exchange and
			// the address bar stays clean. Preserve any path the user deep-linked to.
			window.history.replaceState({}, document.title, window.location.pathname);
		}
	} catch {
		// A stale/invalid callback (e.g. a reused code on a manual reload) just
		// leaves us unauthenticated — the login gate renders and the user retries.
	}
	state.isAuthenticated = await c.isAuthenticated();
	state.profile = state.isAuthenticated ? toProfile(await c.getUser()) : null;
	state.ready = true;
	// Real-auth path: only probe /me once signed in (the call needs a bearer).
	if (state.isAuthenticated) void loadMe();
	else state.meReady = true;
}

// Kick off the redirect login (Google via Auth0). Returns once the browser is
// navigating away; the page reloads on the callback and initAuth() finishes it.
export async function login(): Promise<void> {
	if (!browser || DEV_BYPASS) return; // bypass: already "authenticated", no gate
	await getClient().loginWithRedirect();
}

// Clear the local session and bounce back to the SPA origin (a registered
// allowed-logout URL). The store resets when the fresh page loads.
export function logout(): void {
	if (!browser || DEV_BYPASS) return; // bypass: nothing to clear, no Auth0 session
	void getClient().logout({ logoutParams: { returnTo: window.location.origin } });
}

// getTokenSilently() errors that mean "the silent path can't proceed without a
// user-visible redirect": no Auth0 session (login_required), an un-granted scope
// (consent_required), or no refresh token to refresh with (missing_refresh_token).
// For these we bounce through an interactive Auth0 redirect rather than letting the
// error surface as a dead end (e.g. the onboarding wizard's "couldn't load the
// marketplace catalog" banner). Any other error (network, timeout) just propagates.
const REAUTH_ERRORS = new Set(['login_required', 'consent_required', 'missing_refresh_token']);
const REAUTH_GUARD = 'sayano_reauth_once';

// The access token for an API/WS call. getTokenSilently() returns the cached
// token until it nears expiry, then transparently refreshes it (useRefreshTokens),
// so callers can `await getToken()` on every request cheaply. On a recoverable
// auth error it triggers ONE interactive re-login (guarded against redirect loops)
// instead of throwing; otherwise throws (callers only invoke this behind the gate).
export async function getToken(): Promise<string> {
	if (!browser) throw new Error('getToken() is browser-only');
	if (DEV_BYPASS) return ''; // no token in bypass — callers use authHeaders/wsAuthQuery
	try {
		const token = await getClient().getTokenSilently();
		// Silent path worked → clear the one-shot guard so a *later* genuine expiry
		// can re-trigger the interactive recovery on a future call.
		sessionStorage.removeItem(REAUTH_GUARD);
		return token;
	} catch (e) {
		const code = (e as { error?: string } | null)?.error;
		if (code && REAUTH_ERRORS.has(code) && !sessionStorage.getItem(REAUTH_GUARD)) {
			// One interactive redirect per page load: if we come back and the silent
			// path STILL fails, the guard is set so we fall through to throw rather
			// than loop, and callers degrade gracefully. loginWithRedirect navigates
			// away, so the await below does not resolve in practice.
			sessionStorage.setItem(REAUTH_GUARD, '1');
			await getClient().loginWithRedirect();
		}
		throw e;
	}
}

// The single place identity is attached to a manager call, dual-mode so callers
// (api.ts REST, ws-client WS) never branch on the bypass themselves.
//   • real Auth0 → `Authorization: Bearer <jwt>`
//   • dev bypass → `x-sayano-user: <devUser>` (the manager's token-less path)
export async function authHeaders(): Promise<Record<string, string>> {
	if (DEV_BYPASS) return { 'x-sayano-user': devUser() };
	return { Authorization: `Bearer ${await getToken()}` };
}

// WS identity travels on the URL (browsers can't set WS headers): the access
// token in real mode, ?dev_user= in the bypass. Returns the query fragment.
export async function wsAuthQuery(): Promise<string> {
	if (DEV_BYPASS) return `dev_user=${encodeURIComponent(devUser())}`;
	return `access_token=${encodeURIComponent(await getToken())}`;
}

// Reactive getters (function exports keep the `$state` proxy live across the
// module boundary — a bare `export const` would snapshot the value once).
export function isAuthenticated(): boolean {
	return state.isAuthenticated;
}

export function authReady(): boolean {
	return state.ready;
}

export function profile(): AuthProfile | null {
	return state.profile;
}

// Onboarding store accessors (roadmap.md #4). `me()` is the /me snapshot, used
// by the layout gate to decide between the wizard and the app shell. `setMe()`
// is exposed so the wizard's Finish handler can apply the markOnboarded()
// response without a second roundtrip (and the layout re-renders reactively).
export function me(): Me | null {
	return state.me;
}

export function meReady(): boolean {
	return state.meReady;
}

// True when the last /me attempt (incl. retries) failed — the layout shows a
// 'reconnect' state rather than assuming the user isn't onboarded.
export function meErrored(): boolean {
	return state.meError;
}

export function setMe(next: Me): void {
	state.me = next;
	state.meReady = true;
}
